import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Plus, CreditCard, Building2, Trash2, Check, Star,
  Wallet, DollarSign, Sparkles
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import PageHeader from '../components/PageHeader';
import { SimsConfirmDialog } from '../components/SimsDialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Payment method icons and styling
const methodConfig = {
  bank_account: {
    icon: Building2,
    color: 'from-[#2B4C7E] to-[#2B4C7E]',
    bg: 'from-[#EBF0F5] to-[#EBF0F5]',
    border: 'border-[#D7E0EA]',
    label: 'Bank Account'
  },
  paypal: {
    icon: () => (
      <svg viewBox="0 0 24 24" className="h-6 w-6" fill="currentColor">
        <path d="M7.076 21.337H2.47a.641.641 0 0 1-.633-.74L4.944.901C5.026.382 5.474 0 5.998 0h7.46c2.57 0 4.578.543 5.69 1.81 1.01 1.15 1.304 2.42 1.012 4.287-.023.143-.047.288-.077.437-.983 5.05-4.349 6.797-8.647 6.797h-2.19c-.524 0-.968.382-1.05.9l-1.12 7.106zm14.146-14.42a3.35 3.35 0 0 0-.607-.541c-.013.076-.026.175-.041.254-.93 4.778-4.005 7.201-9.138 7.201h-2.19a.563.563 0 0 0-.556.479l-1.187 7.527h3.906a.488.488 0 0 0 .482-.407l.986-6.244h2.19c4.18 0 7.01-2.262 7.726-6.746.216-1.35.09-2.516-.571-3.523z"/>
      </svg>
    ),
    color: 'from-[#2B4C7E] to-[#1E3A63]',
    bg: 'from-[#EBF0F5] to-sky-50',
    border: 'border-[#B9C7D6]',
    label: 'PayPal'
  },
  cashapp: {
    icon: DollarSign,
    color: 'from-green-500 to-emerald-500',
    bg: 'from-green-50 to-emerald-50',
    border: 'border-green-200',
    label: 'Cash App'
  },
  venmo: {
    icon: () => (
      <svg viewBox="0 0 24 24" className="h-6 w-6" fill="currentColor">
        <path d="M19.5 3c.6 1 .9 2.1.9 3.4 0 4.2-3.6 9.7-6.5 13.6H7.3L4.5 3.3l5.7-.5 1.6 12.9c1.5-2.4 3.3-6.2 3.3-8.8 0-1.3-.2-2.2-.6-2.9l5-.9z"/>
      </svg>
    ),
    color: 'from-[#2B4C7E] to-[#2B4C7E]',
    bg: 'from-[#EBF0F5] to-[#EBF0F5]',
    border: 'border-[#D7E0EA]',
    label: 'Venmo'
  }
};

// Plumbob component
const Plumbob = ({ color = 'blue' }) => (
  <motion.svg
    viewBox="0 0 24 24"
    className="w-6 h-6"
    animate={{ rotateY: [0, 180, 360] }}
    transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
  >
    <defs>
      <linearGradient id={`plumbob-pm-${color}`} x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor={color === 'blue' ? '#60a5fa' : '#4ade80'} />
        <stop offset="100%" stopColor={color === 'blue' ? '#3b82f6' : '#22c55e'} />
      </linearGradient>
    </defs>
    <polygon points="12,2 22,12 12,22 2,12" fill={`url(#plumbob-pm-${color})`} stroke={color === 'blue' ? '#2563eb' : '#16a34a'} strokeWidth="1" />
  </motion.svg>
);

export default function PaymentMethods() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [methods, setMethods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedMethod, setSelectedMethod] = useState(null);
  const [saving, setSaving] = useState(false);
  
  const [formData, setFormData] = useState({
    method_type: 'bank_account',
    account_name: '',
    routing_number: '',
    account_number: '',
    paypal_email: '',
    cashapp_username: '',
    venmo_username: '',
    is_default: false
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    const token = localStorage.getItem('session_token');
    if (!token) {
      navigate('/');
      return;
    }

    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [methodsRes, userRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/payment-methods`, { headers }),
        axios.get(`${BACKEND_URL}/api/auth/me`, { headers })
      ]);

      setMethods(methodsRes.data);
      setUser(userRes.data);
    } catch (error) {
      console.error('Failed to load payment methods:', error);
      toast.error('Failed to load payment methods');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    const token = localStorage.getItem('session_token');
    setSaving(true);

    try {
      await axios.post(
        `${BACKEND_URL}/api/payment-methods`,
        formData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('🎉 Payment method added!');
      setAddModalOpen(false);
      setFormData({
        method_type: 'bank_account',
        account_name: '',
        routing_number: '',
        account_number: '',
        paypal_email: '',
        cashapp_username: '',
        venmo_username: '',
        is_default: false
      });
      loadData();
    } catch (error) {
      console.error('Failed to add payment method:', error);
      toast.error(error.response?.data?.detail || 'Failed to add payment method');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedMethod) return;
    
    const token = localStorage.getItem('session_token');
    try {
      await axios.delete(
        `${BACKEND_URL}/api/payment-methods/${selectedMethod.payment_method_id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Payment method removed');
      setDeleteDialogOpen(false);
      setSelectedMethod(null);
      loadData();
    } catch (error) {
      console.error('Failed to delete:', error);
      toast.error('Failed to delete payment method');
    }
  };

  const setDefault = async (methodId) => {
    const token = localStorage.getItem('session_token');
    try {
      await axios.put(
        `${BACKEND_URL}/api/payment-methods/${methodId}/default`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Default payment method updated');
      loadData();
    } catch (error) {
      console.error('Failed to set default:', error);
      toast.error('Failed to update default');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#EBF0F5] via-[#EBF0F5] to-[#EBF0F5]">
        <motion.div className="text-center" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
          <div className="flex justify-center mb-4">
            <Plumbob color="blue" />
          </div>
          <p className="text-gray-600 font-medium">Loading payment methods...</p>
        </motion.div>
      </div>
    );
  }

  const config = methodConfig[formData.method_type];

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#EBF0F5] via-[#EBF0F5] to-[#EBF0F5]" data-testid="payment-methods-page">
      <PageHeader 
        title="Payment Methods" 
        user={user}
        titleIcon={<Wallet className="h-6 w-6 text-[#2B4C7E]" />}
        actions={
          <Button 
            onClick={() => setAddModalOpen(true)}
            className="bg-gradient-to-r from-[#2B4C7E] to-[#2B4C7E] text-white rounded-full hover:from-[#2B4C7E] hover:to-[#2B4C7E] shadow-lg"
            data-testid="add-method-btn"
          >
            <Plus className="h-5 w-5 mr-2" />
            Add Payment Method
          </Button>
        }
      />

      <main className="max-w-4xl mx-auto p-4 md:p-8">
        {methods.length === 0 ? (
          <motion.div 
            className="text-center py-16"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
          >
            <div className="w-24 h-24 bg-gradient-to-br from-[#EBF0F5] to-[#EBF0F5] rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg">
              <CreditCard className="h-12 w-12 text-[#2B4C7E]" strokeWidth={1.5} />
            </div>
            <h3 className="text-2xl font-bold text-gray-700 mb-3">No Payment Methods Yet</h3>
            <p className="text-gray-500 mb-6 max-w-md mx-auto">
              Link your bank account, PayPal, CashApp, or Venmo to manage deposits and distributions.
            </p>
            <Button
              onClick={() => setAddModalOpen(true)}
              className="bg-gradient-to-r from-[#2B4C7E] to-[#2B4C7E] text-white rounded-full shadow-lg"
            >
              <Plus className="h-5 w-5 mr-2" />
              Add Your First Payment Method
            </Button>
          </motion.div>
        ) : (
          <div className="space-y-4">
            <AnimatePresence>
              {methods.map((method, index) => {
                const cfg = methodConfig[method.method_type] || methodConfig.bank_account;
                const Icon = cfg.icon;
                
                return (
                  <motion.div
                    key={method.payment_method_id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    transition={{ delay: index * 0.1 }}
                  >
                    <Card className={`p-6 rounded-3xl border-2 ${cfg.border} bg-gradient-to-br ${cfg.bg} shadow-lg`}>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className={`w-14 h-14 bg-gradient-to-br ${cfg.color} rounded-2xl flex items-center justify-center shadow-lg text-white`}>
                            {typeof Icon === 'function' ? <Icon /> : <Icon className="h-7 w-7" />}
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <h3 className="font-bold text-gray-800">{method.account_name}</h3>
                              {method.is_default && (
                                <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs font-medium rounded-full flex items-center gap-1">
                                  <Star className="h-3 w-3" /> Default
                                </span>
                              )}
                            </div>
                            <p className="text-sm text-gray-500">{cfg.label}</p>
                            <p className="text-sm font-mono text-gray-600">{method.account_identifier}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {!method.is_default && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => setDefault(method.payment_method_id)}
                              className="rounded-full"
                            >
                              <Check className="h-4 w-4 mr-1" />
                              Set Default
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setSelectedMethod(method);
                              setDeleteDialogOpen(true);
                            }}
                            className="rounded-full text-red-500 hover:text-red-600 hover:bg-red-50"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </Card>
                  </motion.div>
                );
              })}
            </AnimatePresence>
          </div>
        )}
      </main>

      {/* Add Payment Method Modal */}
      <Dialog open={addModalOpen} onOpenChange={setAddModalOpen}>
        <DialogContent className="max-w-md bg-gradient-to-br from-white via-[#EBF0F5]/30 to-[#EBF0F5]/30 border-2 border-[#EBF0F5] rounded-3xl">
          <DialogHeader className="flex flex-row items-center gap-2">
            <Plumbob color="blue" />
            <DialogTitle className="bg-gradient-to-r from-[#2B4C7E] to-[#2B4C7E] bg-clip-text text-transparent">
              Add Payment Method
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Method Type</label>
              <Select value={formData.method_type} onValueChange={(v) => setFormData({ ...formData, method_type: v })}>
                <SelectTrigger className="rounded-xl">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="bank_account">
                    <div className="flex items-center gap-2">
                      <Building2 className="h-4 w-4 text-[#2B4C7E]" />
                      Bank Account
                    </div>
                  </SelectItem>
                  <SelectItem value="paypal">
                    <div className="flex items-center gap-2">
                      <DollarSign className="h-4 w-4 text-[#2B4C7E]" />
                      PayPal
                    </div>
                  </SelectItem>
                  <SelectItem value="cashapp">
                    <div className="flex items-center gap-2">
                      <DollarSign className="h-4 w-4 text-green-500" />
                      Cash App
                    </div>
                  </SelectItem>
                  <SelectItem value="venmo">
                    <div className="flex items-center gap-2">
                      <DollarSign className="h-4 w-4 text-[#2B4C7E]" />
                      Venmo
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Account Name</label>
              <Input
                placeholder="My Personal Account"
                value={formData.account_name}
                onChange={(e) => setFormData({ ...formData, account_name: e.target.value })}
                className="rounded-xl"
              />
            </div>

            {formData.method_type === 'bank_account' && (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Routing Number</label>
                  <Input
                    placeholder="123456789"
                    value={formData.routing_number}
                    onChange={(e) => setFormData({ ...formData, routing_number: e.target.value })}
                    className="rounded-xl font-mono"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Account Number</label>
                  <Input
                    placeholder="1234567890"
                    value={formData.account_number}
                    onChange={(e) => setFormData({ ...formData, account_number: e.target.value })}
                    className="rounded-xl font-mono"
                  />
                </div>
              </>
            )}

            {formData.method_type === 'paypal' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">PayPal Email</label>
                <Input
                  type="email"
                  placeholder="you@email.com"
                  value={formData.paypal_email}
                  onChange={(e) => setFormData({ ...formData, paypal_email: e.target.value })}
                  className="rounded-xl"
                />
              </div>
            )}

            {formData.method_type === 'cashapp' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Cash App Username</label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">$</span>
                  <Input
                    placeholder="username"
                    value={formData.cashapp_username}
                    onChange={(e) => setFormData({ ...formData, cashapp_username: e.target.value })}
                    className="rounded-xl pl-7"
                  />
                </div>
              </div>
            )}

            {formData.method_type === 'venmo' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Venmo Username</label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">@</span>
                  <Input
                    placeholder="username"
                    value={formData.venmo_username}
                    onChange={(e) => setFormData({ ...formData, venmo_username: e.target.value })}
                    className="rounded-xl pl-7"
                  />
                </div>
              </div>
            )}

            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.is_default}
                onChange={(e) => setFormData({ ...formData, is_default: e.target.checked })}
                className="w-4 h-4 rounded border-gray-300 text-[#2B4C7E] focus:ring-[#2B4C7E]"
              />
              <span className="text-sm text-gray-700">Set as default payment method</span>
            </label>
          </div>

          <DialogFooter className="mt-6">
            <Button
              onClick={handleSave}
              disabled={saving || !formData.account_name}
              className="w-full bg-gradient-to-r from-[#2B4C7E] to-[#2B4C7E] text-white rounded-full py-6"
            >
              {saving ? (
                <motion.div animate={{ rotate: 360 }} transition={{ duration: 1, repeat: Infinity, ease: 'linear' }} className="flex items-center gap-2">
                  <Sparkles className="h-5 w-5" />
                  Adding...
                </motion.div>
              ) : (
                <>
                  <CreditCard className="h-5 w-5 mr-2" />
                  Add Payment Method
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <SimsConfirmDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={handleDelete}
        title="Remove Payment Method?"
        message={`Are you sure you want to remove "${selectedMethod?.account_name}"? This action cannot be undone.`}
        confirmText="Yes, Remove"
        cancelText="Keep It"
        variant="error"
      />
    </div>
  );
}
